import { useMemo } from 'react';

/* eslint-disable @typescript-eslint/no-explicit-any */
import type { VerificationStatus } from '@hotjar/webapp/domains/activation/tracking-code/types';
import type { AccountDTO as Account } from '@hotjar/account';
import { PAYMENT_ORIGINS } from '@hotjar/webapp/domains/billing/constants';
import type { SegmentTrackData } from '@hotjar/webapp/domains/common/tracking/segment';
import {
  reset,
  getAnonymousUserID,
  setAnonymousUserID,
} from '@hotjar/webapp/domains/common/tracking/segment';
import { useCurrentSite } from '@hotjar/site';
import type { Site } from '@hotjar/site';
import { useCurrentUser } from '@hotjar/user';
import type { SignupTrackingData } from '../signup-step-progress';
import * as header from '../header';
import * as googleAnalytics from '../tools/google-analytics';
import * as facebook from '../tools/facebook';
import * as utils from '../utils';
import { experimentViewed } from '../experiment-viewed';
import { optimizelyIntegration } from '../optimizely-integration';
import { alias } from '../alias';
import { tagRecording } from '../tag-recording';
import { trackClick } from '../track-click';
import type { SurveyTemplateSearchedArgs } from '../survey-templates-search';
import type { UserCreatedData } from '../user-created';
import { download } from '../download';
import { viewElement } from '../view-element';
import { elementLoadingTime } from '../element-loading-time';
import { formSubmit } from '../form-submit';
import { itemShared } from '../item-shared';
import { onboardingEnded, onboardingStarted } from '../onboarding';
import { trackingCodeVerificationRequest } from '../tracking-code-verification-request';
import { trackingCodeVerificationAttempt } from '../tracking-code-verification-attempt';
import { trackingCodeInstallerOpened } from '../tracking-code-installer-opened';
import { companyDetailsFormSubmitted } from '../company-details-form-submitted';
import { logoutIfAlreadyIdentified } from '../logout-if-already-identified';
import { withContext } from '../with-context';
import { loadedModal } from '../loaded-modal';
import { closedModal } from '../closed-modal';
import {
  signupStepLoaded,
  signupStepSubmitted,
  signupStepCompleted,
} from '../signup-step-progress';
import { emailVerificationAttempted, emailVerificationRequest } from '../email-verification';
import { surveyTemplateSearched } from '../survey-templates-search';
import { userCreated, userCreatedError } from '../user-created';

export interface TrackFunctions {
  alias: (userId: number) => Promise<void>;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  download: (item: string, fileType: string, groupData: any, additionalData?: any) => void;
  add(item: 'Organization' | 'Site'): void;
  tagRecording: (tagName: string) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  click: (eventName: string, uiPosition: string, groupData: any, additionalData?: any) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  viewElement: (
    eventName: string,
    uiPosition: string,
    groupData: any,
    additionalData?: any,
  ) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  elementLoadingTime: (
    eventName: string,
    uiPosition: string,
    loadingTime: number,
    groupData: any,
    additionalData?: any,
  ) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  formSubmit: (
    formName: string,
    submitSuccess: boolean,
    groupData: any,
    additionalData?: any,
  ) => Promise<any>;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  itemShared: (data: {
    item?: string;
    recordingId?: number;
    relevanceScore?: number;
    sharedStatus?: 'On' | 'Off';
  }) => Promise<any>;
  withContext: (context: Record<string, unknown>, fn: () => Promise<unknown>) => Promise<unknown>;
  clickedUpsell: () => void;
  getGroupDataForAccount: (account: Account) => utils.AccountGroupingData | undefined;
  getGroupDataForSite: (site: utils.TrackingSite) => utils.SiteGroupingData | undefined;
  getPermissionLevelForSite: (site: utils.SiteAccess) => utils.PermissionLevelLabel | undefined;
  clickedTrackingActive: () => void;
  optimizelyIntegration: (step: string) => void;
  paymentOrigins: Record<string, string>;
  experimentAssigned: (experimentId: string, experimentTargetId: string, variationId: any) => void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  experimentViewed: (
    experimentId: string,
    experimentTargetId: string,
    variationId: any,
    userId?: any,
  ) => void;
  loadedModal: (
    name: string,
    groupData: utils.AccountGroupingData | utils.SiteGroupingData | undefined,
    data?: SegmentTrackData,
  ) => void;
  closedModal: (
    name: string,
    groupData: utils.AccountGroupingData | utils.SiteGroupingData | undefined,
    data?: SegmentTrackData,
  ) => void;
  hotjarInstallerOpened: (
    site: utils.TrackingSite,
    source: string,
    action: 'install' | 'verify' | 'add-url',
  ) => void;
  trackTCVerificationRequest: (site: utils.TrackingSite) => void;
  trackTCVerificationAttempt: (site: utils.TrackingSite, status: VerificationStatus) => void;
  companyDetailsFormSubmitted: (params: {
    success: boolean;
    failureReason?: string;
    taxFieldPresent: boolean;
    taxFieldFilled: boolean;
    countrySelectChanged: boolean;
  }) => void;
  signupStepLoaded: (data: SignupTrackingData) => void;
  signupStepSubmitted: (data: SignupTrackingData) => void;
  signupStepCompleted: (data: SignupTrackingData) => void;
  emailVerificationRequest: () => void;
  emailVerificationAttempted: (isValid: boolean, method: string) => void;
  login: () => void;
  logout: () => void;
  logoutIfAlreadyIdentified: () => void;
  resetAndKeepAnonymousId: () => void;
  onboardingStarted: (site: Site) => void;
  onboardingEnded: () => void;
  surveyTemplateSearched: (params: SurveyTemplateSearchedArgs) => void;
  getAnonymousUserID: () => Promise<string | unknown>;
  userCreated: (data: UserCreatedData) => Promise<void>;
  userCreatedError: (err: string) => Promise<void>;
}

export const useTrack = (): TrackFunctions => {
  const { data: currentSite } = useCurrentSite();
  const { data: currentUser } = useCurrentUser();

  return useMemo(() => {
    return {
      alias,

      tagRecording: (tagName) => tagRecording({ name: tagName }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      click: (eventName, uiPosition, groupData, additionalData) =>
        trackClick({ name: eventName, uiPosition, groupData, data: additionalData }),

      add: (item) => googleAnalytics.trackCreatedItemEvent(`Added ${item}`),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      download: (item, fileType, groupData, additionalData) =>
        download({ item, fileType, groupData, data: additionalData }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      viewElement: (eventName, uiPosition, groupData, additionalData) =>
        viewElement({ name: eventName, uiPosition, groupData, data: additionalData }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      elementLoadingTime: (
        eventName,
        uiPosition,
        loadingTimeInSeconds,
        groupData,
        additionalData,
      ) =>
        elementLoadingTime({
          name: eventName,
          uiPosition,
          loadingTimeInSeconds,
          groupData,
          data: additionalData,
        }),

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      formSubmit: (name, submitSuccess, groupData, data) =>
        formSubmit({ name, submitSuccess, groupData, data }),

      itemShared: (data) => (currentSite ? itemShared(data, currentSite) : Promise.resolve()),

      withContext,

      paymentOrigins: PAYMENT_ORIGINS,

      clickedUpsell: header.clickedUpsell,

      getGroupDataForAccount: utils.extractAccountGroupingProps,

      getGroupDataForSite: utils.extractSiteGroupingProps,

      getPermissionLevelForSite: utils.extractPermissionLevel,

      clickedTrackingActive: header.clickedTrackingActive,

      optimizelyIntegration: (step) => {
        if (currentSite) {
          optimizelyIntegration(step, currentSite);
        }
      },

      // FIXME: Please fix this. You may refer to CFE-266 for more details.
      // eslint-disable-next-line func-params-args/func-params
      experimentViewed: (experimentId, experimentTargetId, variationId, userId) => {
        experimentViewed({
          experimentId,
          experimentTargetId,
          variationId,
          userId,
          site: currentSite,
        });
      },

      experimentAssigned: (experimentId, experimentTargetId, variationId) => {
        experimentViewed({
          experimentId,
          experimentTargetId,
          variationId,
          userId: String(currentUser?.id),
          site: currentSite,
        });
      },

      onboardingStarted: (site: Site) =>
        onboardingStarted({ userId: String(currentUser?.id), site }),

      onboardingEnded: () =>
        onboardingEnded({ userId: String(currentUser?.id), site: currentSite }),

      loadedModal,

      closedModal,

      trackTCVerificationRequest: trackingCodeVerificationRequest,

      trackTCVerificationAttempt: trackingCodeVerificationAttempt,

      hotjarInstallerOpened: (site, source, action) =>
        trackingCodeInstallerOpened(site, {
          source,
          action,
        }),

      companyDetailsFormSubmitted: (params) => {
        if (currentSite) {
          companyDetailsFormSubmitted(params, currentSite.account);
        }
      },

      signupStepSubmitted,

      signupStepCompleted,

      signupStepLoaded,

      emailVerificationRequest,

      emailVerificationAttempted,

      login: () => facebook.trackLogin(),

      logout: () => reset(),

      logoutIfAlreadyIdentified: () => logoutIfAlreadyIdentified(),

      resetAndKeepAnonymousId: async () => {
        const id = await getAnonymousUserID();
        reset();
        setAnonymousUserID(id);
      },
      surveyTemplateSearched,

      getAnonymousUserID,

      userCreated,

      userCreatedError,
    };
  }, [currentSite, currentUser]);
};
